import {
  ArrowDown,
  ArrowUp,
  Copy,
  Download,
  ExternalLink,
  GitFork,
  LineChart as LineIcon,
  Maximize2,
  Pin,
  RotateCw,
  Table2,
  X,
} from 'lucide-react'
import { type CSSProperties, type ReactNode, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'wouter'
import { cell, fmt, pct } from '../lib/format'
import type { ChartExhibit, Exhibit, TableExhibit, TreeExhibit, TreeNode, ViewLink } from '../lib/types'
import { Chart } from './Chart'
import { CompactTree, DriverDiagram, delta, share, viewHref } from './DriverTree'
import { PeriodBand, PeriodChip, periodCell } from './Periods'
import { useToast } from './ui'

export function linkFor(v: ViewLink): string {
  const p = new URLSearchParams({ measure: v.measure })
  if (v.rows.length) p.set('rows', v.rows.join(','))
  if (v.cols) p.set('cols', v.cols)
  for (const [l, ids] of Object.entries(v.filters)) for (const id of ids) p.append('f', `${l}:${id}`)
  if (v.version) p.set('version', v.version)
  if (v.compare) p.set('compare', v.compare)
  return `/views?${p}`
}

const hrefOf = (e: Exhibit) => (e.kind === 'tree' ? viewHref(e.root.measure, e.root.coord, e.version.key, e.compare?.key) : linkFor(e.view))

// ---------------------------------------------------------------- export (copy / CSV)

const raw = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(Math.round(v * 1e6) / 1e6))

/** The exhibit as a grid of plain values: pastes cleanly into Excel or Sheets. */
export function toGrid(e: Exhibit): string[][] {
  if (e.kind === 'table') {
    const head = [...e.rowLevels.map(l => l.label), ...e.columns.map(c => c.label)]
    const rows = e.rows.map(r => [...e.rowLevels.map((_, i) => r.labels[i] ?? ''), ...r.values.map(raw)])
    if (e.rows.some(r => r.base)) {
      head.push(...e.columns.map(c => `${c.label} (${e.compare?.label ?? 'compare'})`))
      for (const [i, row] of rows.entries()) row.push(...(e.rows[i]?.base ?? e.columns.map(() => null)).map(raw))
    }
    return [head, ...rows]
  }
  if (e.kind === 'chart')
    return [['Period', ...e.series.map(s => s.label)], ...e.x.map((p, i) => [p.label, ...e.series.map(s => raw(s.values[i]))])]
  const out: string[][] = [['Driver', 'Level', e.version.label, ...(e.compare ? [e.compare.label, 'Change'] : [])]]
  const walk = (n: TreeNode, d: number) => {
    out.push([`${'  '.repeat(d)}${n.label}`, String(d), raw(n.value), ...(e.compare ? [raw(n.base), raw(delta(n))] : [])])
    for (const c of n.children) walk(c, d + 1)
  }
  walk(e.root, 0)
  return out
}

const tsv = (g: string[][]) => g.map(r => r.map(c => c.replace(/[\t\n]/g, ' ')).join('\t')).join('\n')
const csv = (g: string[][]) => g.map(r => r.map(c => (/[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(',')).join('\n')
const fileName = (t: string) =>
  `${
    t
      .replace(/[^\w\s-]+/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .toLowerCase() || 'exhibit'
  }.csv`

function useExport(e: Exhibit) {
  const toast = useToast()
  return {
    copy: async () => {
      try {
        await navigator.clipboard.writeText(tsv(toGrid(e)))
        toast('Copied', 'Paste into Excel or Sheets: values stay numbers')
      } catch {
        toast('Could not copy', 'Your browser blocked clipboard access; use Download CSV instead')
      }
    },
    download: () => {
      const url = URL.createObjectURL(new Blob([csv(toGrid(e))], { type: 'text/csv' }))
      const a = document.createElement('a')
      a.href = url
      a.download = fileName(e.title)
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    },
  }
}

// ---------------------------------------------------------------- tables

/** Up/down in neutral colors, or favorable green and unfavorable red when the direction that's good is known. */
export function deltaClass(v: number, good?: 'up' | 'down' | null) {
  if (!good) return v > 0 ? 'x-up' : 'x-down'
  return (good === 'up' ? v > 0 : v < 0) ? 'x-good' : 'x-bad'
}

function DeltaCell({
  v,
  format,
  percent = false,
  good,
}: {
  v: number | null
  format: string
  percent?: boolean
  good?: 'up' | 'down' | null
}) {
  if (v === null) return <span className="faint">—</span>
  if (Math.abs(v) < (percent ? 0.0005 : 0.5)) return <span className="faint">{percent ? '0.0%' : '—'}</span>
  return <span className={deltaClass(v, good)}>{percent ? pct(v, 1, true) : fmt(v, format, true)}</span>
}

function ExTable({
  t,
  limit,
  full = false,
  onRow,
  activeRow,
  good,
}: {
  t: TableExhibit
  limit?: number
  full?: boolean
  onRow?: (key: string[], label: string) => void
  activeRow?: string | null
  good?: 'up' | 'down' | null
}) {
  const [sort, setSort] = useState<{ col: number; dir: 1 | -1 } | null>(null)
  const f = t.measure.format
  const body = t.rows.filter(r => r.kind === 'row')
  const tail = t.rows.filter(r => r.kind !== 'row')
  const sorted = useMemo(() => {
    if (!sort) return body
    const k = sort.col
    return [...body].sort((a, b) => {
      const av = k < 0 ? (a.labels[-k - 1] ?? '') : (a.values[k] ?? Number.NEGATIVE_INFINITY)
      const bv = k < 0 ? (b.labels[-k - 1] ?? '') : (b.values[k] ?? Number.NEGATIVE_INFINITY)
      return (typeof av === 'string' ? av.localeCompare(String(bv)) : (av as number) - (bv as number)) * sort.dir
    })
  }, [body, sort])
  const shown = limit && sorted.length > limit ? sorted.slice(0, limit) : sorted
  const hiddenRows = sorted.length - shown.length
  const num = (v: number | null) => (v === null ? '' : full ? cell(v, f) : fmt(v, f))
  const head = (label: ReactNode, col: number, right: boolean, extra = '', style?: CSSProperties) => (
    <th
      className={`${right ? 'r' : ''} ${extra}`}
      style={style}
      aria-sort={sort?.col === col ? (sort.dir > 0 ? 'ascending' : 'descending') : undefined}
    >
      {full ? (
        <button
          type="button"
          className="x-sort"
          onClick={() => setSort(s => (s?.col === col ? (s.dir < 0 ? { col, dir: 1 } : null) : { col, dir: col < 0 ? 1 : -1 }))}
        >
          {label}
          {sort?.col === col && (sort.dir > 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
        </button>
      ) : (
        label
      )}
    </th>
  )
  const row = (r: TableExhibit['rows'][number], i: number) => (
    <tr
      key={`${r.kind}-${r.key.join('/')}-${i}`}
      className={`k-${r.kind} ${onRow && r.kind === 'row' ? 'clickable' : ''} ${activeRow && r.key.join('/') === activeRow ? 'active' : ''}`}
      onClick={onRow && r.kind === 'row' ? () => onRow(r.key, r.labels.filter(Boolean).join(' · ')) : undefined}
      title={onRow && r.kind === 'row' ? 'Why? Show the drivers for this row' : undefined}
    >
      {t.rowLevels.map((_, li) => (
        <td key={li} className="x-label">
          {r.labels[li]}
        </td>
      ))}
      {t.columns.map((c, ci) => {
        const v = r.values[ci] ?? null
        const b = r.base?.[ci] ?? null
        if (c.kind === 'delta')
          return (
            <td key={c.id} className="r num c-delta">
              <DeltaCell v={v} format={f} good={good} />
            </td>
          )
        if (c.kind === 'delta_pct')
          return (
            <td key={c.id} className="r num c-delta_pct">
              <DeltaCell v={v} format={f} percent good={good} />
            </td>
          )
        if (c.kind === 'share')
          return (
            <td key={c.id} className="r num x-share c-share">
              {v === null ? (
                ''
              ) : (
                <>
                  <span className="x-sharebar">
                    <span style={{ width: `${Math.max(1, Math.min(100, Math.abs(v) * 100))}%` }} />
                  </span>
                  {pct(v, 1)}
                </>
              )}
            </td>
          )
        return (
          <td
            key={c.id}
            className={`r num c-${c.kind} ${periodCell(t.columns, ci).className} ${c.kind === 'total' ? 'x-total' : ''}`}
            style={periodCell(t.columns, ci).style}
          >
            {num(v)}
            {b !== null && v !== null && Math.abs(v - b) >= 0.5 && (
              <div className={`x-base ${deltaClass(v - b, good)}`}>{fmt(v - b, f, true)}</div>
            )}
          </td>
        )
      })}
    </tr>
  )
  return (
    <div className={`x-table ${full ? 'full' : 'compact'} ${t.columns.length > 4 ? 'wide' : ''}`}>
      <table>
        <thead>
          <PeriodBand cols={t.columns} lead={t.rowLevels.length} />
          <tr>
            {t.rowLevels.map((l, i) => head(l.label, -i - 1, false))}
            {t.columns.map((c, ci) =>
              head(
                c.label,
                ci,
                true,
                `c-${c.kind} ${periodCell(t.columns, ci).className} ${c.kind === 'total' ? 'x-total' : ''}`,
                periodCell(t.columns, ci).style,
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {shown.map(row)}
          {hiddenRows > 0 && (
            <tr className="x-more">
              <td colSpan={t.rowLevels.length + t.columns.length}>{hiddenRows} more rows · expand to see all</td>
            </tr>
          )}
          {tail.map(row)}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------- card + viewer

const ICON = { table: Table2, chart: LineIcon, tree: GitFork }

function versionsLine(e: Exhibit) {
  if (e.kind === 'chart') return e.series.length > 1 && e.view.compare ? e.series.map(s => s.label).join(' vs ') : e.measure.label
  return e.compare ? `${e.version.label} vs ${e.compare.label}` : e.version.label
}

/** An exhibit in the conversation: compact, with Expand for the full view. */
export function ExhibitCard({
  e,
  onRefresh,
  onRow,
  activeRow,
  good,
  onPin,
}: {
  e: Exhibit
  /** pin this exhibit to a page (live: it recomputes whenever the page opens) */
  onPin?: () => void
  onRefresh?: () => Promise<void>
  onRow?: (key: string[], label: string) => void
  activeRow?: string | null
  /** which direction is favorable, when the exhibit is about one KPI */
  good?: 'up' | 'down' | null
}) {
  const [open, setOpen] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const { copy } = useExport(e)
  const Icon = ICON[e.kind]
  return (
    <figure className="exhibit">
      <header className="exhibit-head">
        <Icon size={14} className="exhibit-icon" />
        <div className="grow" style={{ minWidth: 0 }}>
          <figcaption className="exhibit-title">{e.title}</figcaption>
          {e.caption && <div className="exhibit-caption">{e.caption}</div>}
        </div>
        <button type="button" className="icon-btn" onClick={() => setOpen(true)} aria-label="Expand" title="Expand">
          <Maximize2 size={13} />
        </button>
      </header>
      <div className="exhibit-body">
        {e.kind === 'table' ? (
          <ExTable t={e} limit={e.display ?? 10} onRow={onRow} activeRow={activeRow} good={good} />
        ) : e.kind === 'chart' ? (
          <Chart x={e.x} series={e.series} format={e.measure.format} mode={e.mode} stack={e.stacked} height={190} compact />
        ) : (
          <CompactTree root={e.root} maxDepth={1} good={good} />
        )}
      </div>
      <footer className="exhibit-foot">
        <span className="exhibit-meta">{versionsLine(e)}</span>
        <div className="grow" />
        {onRefresh && (
          <button
            type="button"
            className="link-btn"
            disabled={refreshing}
            title="Recompute from the plan as it is now"
            onClick={async () => {
              setRefreshing(true)
              try {
                await onRefresh()
              } finally {
                setRefreshing(false)
              }
            }}
          >
            <RotateCw size={11} className={refreshing ? 'spin' : undefined} /> Refresh
          </button>
        )}
        {onPin && (
          <button type="button" className="link-btn" onClick={onPin} title="Keep this on one of your pages, live">
            <Pin size={11} /> Pin
          </button>
        )}
        <button type="button" className="link-btn" onClick={copy} title="Copy as a table for Excel or Sheets">
          <Copy size={11} /> Copy
        </button>
        <Link href={hrefOf(e)} className="link-btn">
          <ExternalLink size={11} /> Open in view
        </Link>
        <button type="button" className="link-btn strong" onClick={() => setOpen(true)}>
          <Maximize2 size={11} /> {e.kind === 'tree' ? 'Diagram' : 'Expand'}
        </button>
      </footer>
      {e.kind !== 'tree' && e.notes.length > 0 && <div className="exhibit-notes">{e.notes.join(' · ')}</div>}
      {open && <ExhibitViewer e={e} onClose={() => setOpen(false)} />}
    </figure>
  )
}

/** The exhibit, full screen: a sortable full-precision table, a large chart with its data, or the driver diagram. */
export function ExhibitViewer({ e, onClose }: { e: Exhibit; onClose: () => void }) {
  const { copy, download } = useExport(e)
  const [showData, setShowData] = useState(false)
  useEffect(() => {
    const h = (ev: KeyboardEvent) => ev.key === 'Escape' && onClose()
    window.addEventListener('keydown', h)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', h)
      document.body.style.overflow = prev
    }
  }, [onClose])
  const Icon = ICON[e.kind]
  return createPortal(
    <div className="viewer-bg" onMouseDown={ev => ev.target === ev.currentTarget && onClose()}>
      <div className="viewer" role="dialog" aria-modal="true" aria-label={e.title}>
        <header className="viewer-head">
          <Icon size={16} className="exhibit-icon" />
          <div className="grow" style={{ minWidth: 0 }}>
            <h2>{e.title}</h2>
            <div className="muted small">
              {e.caption ? `${e.caption} · ` : ''}
              {versionsLine(e)}
            </div>
          </div>
          <button type="button" className="btn sm" onClick={copy}>
            <Copy size={12} /> Copy
          </button>
          <button type="button" className="btn sm" onClick={download}>
            <Download size={12} /> CSV
          </button>
          <Link href={hrefOf(e)} className="btn sm" onClick={onClose}>
            <ExternalLink size={12} /> Open in view
          </Link>
          <button type="button" className="btn ghost sm" onClick={onClose} aria-label="Close">
            <X size={15} />
          </button>
        </header>
        <div className={`viewer-body ${e.kind}`}>
          {e.kind === 'table' ? (
            <ExTable t={e} full />
          ) : e.kind === 'chart' ? (
            <div className="stack">
              <Chart
                x={e.x}
                series={e.series}
                format={e.measure.format}
                mode={e.mode}
                stack={e.stacked}
                height={Math.min(460, Math.max(300, window.innerHeight - 320))}
              />
              <div>
                <button type="button" className="link-btn" onClick={() => setShowData(!showData)}>
                  {showData ? 'Hide' : 'Show'} the data
                </button>
                {showData && <ChartData c={e} />}
              </div>
            </div>
          ) : (
            <DriverDiagram exhibit={e as TreeExhibit} />
          )}
        </div>
        {e.kind !== 'tree' && e.notes.length > 0 && <div className="viewer-notes">{e.notes.join(' · ')}</div>}
      </div>
    </div>,
    document.body,
  )
}

function ChartData({ c }: { c: ChartExhibit }) {
  return (
    <div className="x-table full" style={{ marginTop: 8 }}>
      <table>
        <thead>
          <tr>
            <th>Period</th>
            {c.series.map(s => (
              <th key={s.key} className="r">
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {c.x.map((p, i) => (
            <tr key={p.id} className={periodCell(c.x, i).className} style={periodCell(c.x, i).style}>
              <td className="x-label">
                {p.label}
                <PeriodChip status={p.period} />
              </td>
              {c.series.map(s => (
                <td key={s.key} className="r num">
                  {s.values[i] === null ? '' : cell(s.values[i], c.measure.format)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** A running show_* call: the card's frame while the engine computes it. */
export function ExhibitPending({ title, kind }: { title: string; kind: Exhibit['kind'] }) {
  const Icon = ICON[kind]
  return (
    <figure className="exhibit pending" aria-busy="true">
      <header className="exhibit-head">
        <Icon size={14} className="exhibit-icon" />
        <figcaption className="exhibit-title">{title}</figcaption>
      </header>
      <div className="exhibit-body">
        <div className="skel" style={{ height: 12, width: '70%' }} />
        <div className="skel" style={{ height: 12, width: '90%', marginTop: 8 }} />
        <div className="skel" style={{ height: 12, width: '55%', marginTop: 8 }} />
      </div>
    </figure>
  )
}

export { share }
